import React, { useEffect, useState } from "react";
import { Building, ServerCog } from "lucide-react";
import { Task } from "../../types/api";
import {
  useEquipmentStore,
  useOrgUsersStore,
  usePremisesStore,
  useRegionStore,
  useTaskStore,
} from "../../store/store";
import { TASK_MODULE_LIST_FIRE_SAFETY } from "../../constants/modules";

interface AddTaskProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  taskToEdit?: Task | null;
  isEditMode?: boolean;
  isViewMode?: boolean;
}

const AddTask: React.FC<AddTaskProps> = ({
  showModal,
  setShowModal,
  taskToEdit = null,
  isEditMode = false,
  isViewMode = false,
}) => {
  const [task, setTask] = useState("");
  const [topicId, setTopicId] = useState("");
  const [responsiblePersonList, setResponsiblePersonList] = useState("");
  const [frequency, setFrequency] = useState("");
  const [monthlyInterval, setMonthlyInterval] = useState("1");
  const [weeklyInterval, setWeeklyInterval] = useState("1");
  const [premises, setPremises] = useState("");
  const [premisesList, setPremisesList] = useState<
    { id: string; name: string }[]
  >([]);
  const [premisesId, setPremisesId] = useState("");
  const [regionsList, setRegionsList] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [fromDueDate, setFromDueDate] = useState("");
  const [furtherDetails, setFurtherDetails] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [equipmentList, setEquipmentList] = useState<
    { id: string; name: string }[]
  >([]);
  const [responsiblePersons, setResponsiblePersons] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { addTask, updateTask, currentTask } = useTaskStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const { equipmentData } = useEquipmentStore();
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();

  const frequencyOptions = ["One Time", "Daily", "Weekly", "Monthly"];

  useEffect(() => {
    if (!allPremises.length) fetchPremises();
    if (!regions.length) fetchRegions();
    if (!orgUsersData.length) fetchOrgUsers();
  }, [fetchPremises, fetchRegions, fetchOrgUsers]);

  useEffect(() => {
    if (equipmentData && equipmentData.length > 0) {
      const list = equipmentData.map((eq: any) => ({
        id: eq._id,
        name: eq.equipmentName,
      }));
      setEquipmentList(list);
    }
  }, [equipmentData]);

  useEffect(() => {
    if (regions && regions.length > 0) {
      const list = regions.map((reg: any) => ({
        id: reg._id,
        name: reg.regionName,
      }));
      setRegionsList(list);
    }
  }, [regions]);

  useEffect(() => {
    let filteredPremises: { id: string; name: string }[] = [];
    if (allPremises.length > 0) {
      if (selectedRegionId) {
        filteredPremises = allPremises
          .filter((premise: any) => premise.region === selectedRegionId)
          .map((premise: any) => ({
            id: premise._id,
            name: premise.premisesName,
          }));
      } else {
        filteredPremises = allPremises.map((premise: any) => ({
          id: premise._id,
          name: premise.premisesName,
        }));
      }
      const unique = Array.from(
        new Set(filteredPremises.map((p) => JSON.stringify(p)))
      ).map((item) => JSON.parse(item));
      setPremisesList(unique);
    }
  }, [allPremises, selectedRegionId]);

  useEffect(() => {
    if (orgUsersData.length > 0 && selectedRegionId && premisesId) {
      const filtered = orgUsersData
        .filter((user: any) => {
          return (
            user.region &&
            Array.isArray(user.region) &&
            user.region.includes(selectedRegionId) &&
            user.premise &&
            Array.isArray(user.premise) &&
            user.premise.includes(premisesId)
          );
        })
        .map((user: any) => ({
          id: user._id,
          name: `${user.firstName} ${user.lastName}`,
        }));
      setResponsiblePersons(filtered);
    } else {
      setResponsiblePersons([]);
    }
  }, [orgUsersData, selectedRegionId, premisesId]);

  useEffect(() => {
    if (isEditMode && taskToEdit) {
      setTask(taskToEdit.task || "");
      setTopicId(taskToEdit.topic || "");
      setFrequency(taskToEdit.frequency || "");
      setMonthlyInterval(
        taskToEdit.frequency === "Monthly"
          ? taskToEdit.monthlyInterval || "1"
          : "1"
      );
      setWeeklyInterval(
        taskToEdit.frequency === "Weekly"
          ? taskToEdit.weeklyInterval || "1"
          : "1"
      );
      setFromDueDate(taskToEdit.fromDueDate || "");
      setResponsiblePersonList(taskToEdit.reference || "");
      setPremises(taskToEdit.premises || "");
      setPremisesId(taskToEdit.premisesId || "");
      setFurtherDetails(taskToEdit.furtherDetails || "");
      setIsRecurring(taskToEdit.isRecurring || false);
      setSelectedModule(taskToEdit.module || "");
      if (allPremises.length > 0) {
        const premise = allPremises.find(
          (p: any) => p._id === taskToEdit.premisesId
        );
        if (premise) {
          setSelectedRegionId(premise.region || "");
          setPremises(premise.premisesName || "");
        }
      }
    } else if ((isEditMode || isViewMode) && !taskToEdit && currentTask) {
      setTask(currentTask.task || "");
      setTopicId(currentTask.topic || "");
      setFrequency(currentTask.frequency || "");
      setMonthlyInterval(
        currentTask.frequency === "Monthly"
          ? currentTask.monthlyInterval || "1"
          : "1"
      );
      setWeeklyInterval(
        currentTask.frequency === "Weekly"
          ? currentTask.weeklyInterval || "1"
          : "1"
      );
      setFromDueDate(currentTask.fromDueDate || "");
      setResponsiblePersonList(currentTask.reference || "");
      setPremises(currentTask.premises || "");
      setPremisesId(currentTask.premisesId || "");
      setFurtherDetails(currentTask.furtherDetails || "");
      setIsRecurring(currentTask.isRecurring || false);
      setSelectedModule(currentTask.module || "");
      if (allPremises.length > 0) {
        const premise = allPremises.find(
          (p: any) => p._id === currentTask.premisesId
        );
        if (premise) {
          setSelectedRegionId(premise.region || "");
          setPremises(premise.premisesName || "");
        }
      }
    } else {
      setTask("");
      setTopicId("");
      setFrequency("");
      setMonthlyInterval("1");
      setWeeklyInterval("1");
      setFromDueDate("");
      setPremises("");
      setPremisesId("");
      setSelectedRegionId("");
      setResponsiblePersonList("");
      setFurtherDetails("");
      setIsRecurring(false);
      setSelectedModule("");
    }
    setErrors({});
  }, [isEditMode, isViewMode, taskToEdit, currentTask, allPremises]);

  // useEffect(() => {
  //   if (
  //     isViewMode ||
  //     !fromDueDate ||
  //     !isRecurring ||
  //     frequency === "Daily" ||
  //     frequency === "One Time"
  //   ) {
  //     return;
  //   }

  //   const fromDate = new Date(fromDueDate);
  //   let newToDueDate = "";

  //   switch (frequency) {
  //     case "Monthly":
  //       const monthlyInt = parseInt(monthlyInterval || "1");
  //       fromDate.setMonth(fromDate.getMonth() + monthlyInt);
  //       newToDueDate = fromDate.toISOString().split("T")[0];
  //       break;
  //     default:
  //       newToDueDate = "";
  //   }

  //   setToDueDate(newToDueDate);
  // }, [
  //   frequency,
  //   monthlyInterval,
  //   weeklyInterval,
  //   fromDueDate,
  //   isViewMode,
  //   isRecurring,
  // ]);

  useEffect(() => {
    if (frequency === "One Time") {
      setIsRecurring(false);
      setMonthlyInterval("1");
      setWeeklyInterval("1");
    } else if (frequency) {
      setIsRecurring(true);
    }
  }, [frequency]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!task) newErrors.task = "Task is required";
    if (!topicId) newErrors.topic = "Topic is required";
    if (!selectedRegionId) newErrors.region = "Region is required";
    if (!premisesId) newErrors.premises = "Premises is required";
    if (!responsiblePersonList)
      newErrors.reference = "Responsible Person is required";
    if (!frequency) newErrors.frequency = "Frequency is required";
    if (frequency === "Monthly" && !monthlyInterval)
      newErrors.monthlyInterval = "Monthly interval is required";
    if (frequency === "Weekly" && !weeklyInterval)
      newErrors.weeklyInterval = "Weekly interval is required";
    if (!fromDueDate) newErrors.fromDueDate = "From date is required";
    // if (isRecurring && frequency !== "One Time" && !toDueDate)
    //   newErrors.toDueDate = "To date is required";
    if (!furtherDetails)
      newErrors.furtherDetails = "Further details are required";
    if (!selectedModule) newErrors.module = "Module is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const taskData = {
      task,
      topic: topicId,
      reference: responsiblePersonList,
      frequency,
      ...(frequency === "Monthly" && { monthlyInterval }),
      ...(frequency === "Weekly" && { weeklyInterval }),
      fromDueDate,
      // toDueDate:
      //   isRecurring && frequency !== "One Time" ? toDueDate : fromDueDate,
      premises,
      premisesId,
      regionId: selectedRegionId,
      region: regionsList.find(r => r.id === selectedRegionId)?.name || "",
      furtherDetails,
      isRecurring,
      module: selectedModule,
    };

    try {
      if (isEditMode) {
        const taskId = taskToEdit?.id || currentTask?.id;
        if (!taskId) throw new Error("No task ID provided for update");
        await updateTask(taskId, taskData);
      } else {
        await addTask(taskData);
      }
      setShowModal(false);
    } catch (error) {
      console.error("Error saving task:", error);
    }
  };

  const renderModuleSelect = (value: string, isView: boolean) => {
    const selectedModuleName =
      TASK_MODULE_LIST_FIRE_SAFETY.find((m) => m.value === selectedModule)
        ?.label ||
      selectedModule ||
      "-";
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {selectedModuleName}
      </div>
    ) : (
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={selectedModule}
        onChange={(e) => setSelectedModule(e.target.value)}
      >
        <option value="">Select Module</option>
        {TASK_MODULE_LIST_FIRE_SAFETY.map((module) => (
          <option key={module.value} value={module.value}>
            {module.label}
          </option>
        ))}
      </select>
    );
  };

  const renderFrequencySelect = (value: string, isView: boolean) => {
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {value || "-"}
      </div>
    ) : (
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={frequency}
        onChange={(e) => setFrequency(e.target.value)}
      >
        <option value="">Select Frequency</option>
        {frequencyOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  };

  const renderMonthlyIntervalSelect = (value: string, isView: boolean) => {
    if (isView || frequency !== "Monthly") return null;
    return (
      <div>
        <label className="block text-sm font-medium">
          Monthly Interval <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
          value={value}
          onChange={(e) => setMonthlyInterval(e.target.value)}
        >
          {[1, 2].map((num) => (
            <option key={num} value={num.toString()}>
              {num} {num === 1 ? "month" : "months"}
            </option>
          ))}
        </select>
        {errors.monthlyInterval && (
          <p className="text-red-500 text-sm">{errors.monthlyInterval}</p>
        )}
      </div>
    );
  };

  const renderWeeklyIntervalSelect = (value: string, isView: boolean) => {
    if (isView || frequency !== "Weekly") return null;
    return (
      <div>
        <label className="block text-sm font-medium">
          Weekly Interval <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
          value={value}
          onChange={(e) => setWeeklyInterval(e.target.value)}
        >
          {[1, 2, 3, 4].map((num) => (
            <option key={num} value={num.toString()}>
              {num} {num === 1 ? "week" : "weeks"}
            </option>
          ))}
        </select>
        {errors.weeklyInterval && (
          <p className="text-red-500 text-sm">{errors.weeklyInterval}</p>
        )}
      </div>
    );
  };

  const renderRegionSelect = (selectedId: string, isView: boolean) => {
    const selectedName =
      regionsList.find((r) => r.id === selectedId)?.name || selectedId || "-";
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {selectedName}
      </div>
    ) : (
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={selectedId}
        onChange={(e) => {
          setSelectedRegionId(e.target.value);
          setPremisesId("");
          setPremises("");
        }}
      >
        <option value="">Select Region</option>
        {regionsList.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  };

  const renderPremisesSelect = (value: string, isView: boolean) => {
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {value || "-"}
      </div>
    ) : (
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={premisesId}
        onChange={(e) => {
          const selectedPremises = premisesList.find(
            (p) => p.id === e.target.value
          );
          setPremises(selectedPremises?.name || "");
          setPremisesId(e.target.value);
        }}
      >
        <option value="">Select Premises</option>
        {premisesList.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  };

  const renderTopicField = (value: string, isView: boolean) => {
    const isFireSafety = TASK_MODULE_LIST_FIRE_SAFETY.find(
      (m) =>
        m.label.includes("Assets & Equipment") && m.value === selectedModule
    );

    if (isFireSafety) {
      return isView ? (
        <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
          {equipmentList.find((e) => e.id === value)?.name || value || "-"}
        </div>
      ) : (
        <select
          className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
          value={topicId}
          onChange={(e) => setTopicId(e.target.value)}
        >
          <option value="">Select Equipment</option>
          {equipmentList.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      );
    } else {
      return renderInputOrText(value, isView, setTopicId);
    }
  };

  const renderResponsiblePersonSelect = (value: string, isView: boolean) => {
    const selectedUser = orgUsersData.find((u: any) => u._id === value);
    const selectedName = selectedUser
      ? `${selectedUser.firstName} ${selectedUser.lastName}`
      : value || "-";
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {selectedName}
      </div>
    ) : (
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={responsiblePersonList}
        onChange={(e) => setResponsiblePersonList(e.target.value)}
      >
        <option value="">Select Responsible Person</option>
        {responsiblePersons.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  };

  const renderInputOrText = (
    value: string,
    isView: boolean,
    onChange: (value: string) => void
  ) => {
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {value || "-"}
      </div>
    ) : (
      <input
        type="text"
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  /* const renderSelectOrText = (
    value: string,
    options: string[],
    isView: boolean,
    onChangeHandler?: (value: string) => void
  ) => {
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
        {value || "-"}
      </div>
    ) : (
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        value={value}
        onChange={(e) => onChangeHandler?.(e.target.value)}
      >
        <option value="">Select an option</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }; */

  const renderDateInput = (
    value: string,
    onChange: (date: string) => void,
    errorKey: string,
    label: string,
    isView: boolean,
    readOnly: boolean = false
  ) => {
    const today = new Date().toISOString().split("T")[0];
    let minDate: string | undefined = undefined;
    let maxDate: string | undefined = undefined;
    let isReadOnly = readOnly;

    // Base minDate logic
    if (label === "From Due Date") {
      minDate = today;
    }

    // Dynamic Weekly Interval logic
    if (frequency === "Weekly" && label === "To Due Date" && fromDueDate) {
      const fromDate = new Date(fromDueDate);
      const weeks = parseInt(weeklyInterval || "1");

      const max = new Date(fromDate);
      max.setDate(fromDate.getDate() + 7 * weeks - 1); // end of interval
      minDate = fromDueDate;
      maxDate = max.toISOString().split("T")[0];
      isReadOnly = false;
    }

    if (frequency === "Monthly" && label === "To Due Date" && fromDueDate) {
      const fromDate = new Date(fromDueDate);
      const months = parseInt(monthlyInterval || "1");

      const max = new Date(fromDate);
      max.setMonth(fromDate.getMonth() + months);

      // Keep user selection only within this interval window
      minDate = fromDueDate;
      maxDate = max.toISOString().split("T")[0];
      isReadOnly = false;

      // If the current toDueDate is outside this window, reset it
      if (value) {
        const currentTo = new Date(value);
        if (currentTo < fromDate || currentTo > max) {
          onChange("");
        }
      }
    }

    // Default fallback
    if (!minDate) {
      minDate = today;
    }

    return (
      <div>
        <label className="block text-sm font-medium">
          {label} <span className="text-red-500">*</span>
        </label>
        {isView ? (
          <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
            {value || "-"}
          </div>
        ) : (
          <input
            type="date"
            className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            min={minDate}
            max={maxDate}
            readOnly={isReadOnly}
            style={isReadOnly ? { backgroundColor: "#f0f0f0" } : {}}
          />
        )}
        {errors[errorKey] && !isViewMode && (
          <p className="text-red-500 text-sm">{errors[errorKey]}</p>
        )}
      </div>
    );
  };

  const renderTextareaOrText = (value: string, isView: boolean) => {
    return isView ? (
      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50 min-h-[60px]">
        {value || "-"}
      </div>
    ) : (
      <textarea
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        rows={2}
        value={value}
        onChange={(e) => setFurtherDetails(e.target.value)}
      />
    );
  };

  const getNextExecutionDate = () => {
    if (!fromDueDate || !frequency) return "N/A";

    const fromDate = new Date(fromDueDate);
    let nextExecution = "";

    switch (frequency) {
      case "Weekly": {
        const weeks = parseInt(weeklyInterval || "1");
        fromDate.setDate(fromDate.getDate() + 7 * weeks);
        nextExecution = fromDate.toISOString().split("T")[0];
        break;
      }
      case "Monthly": {
        const months = parseInt(monthlyInterval || "1");
        fromDate.setMonth(fromDate.getMonth() + months);
        nextExecution = fromDate.toISOString().split("T")[0];
        break;
      }
      case "Daily": {
        fromDate.setDate(fromDate.getDate() + 1);
        nextExecution = fromDate.toISOString().split("T")[0];
        break;
      }
      case "One Time":
      default:
        nextExecution = "N/A";
        break;
    }

    if (!nextExecution) return "N/A";

    const nextDateObj = new Date(nextExecution);
    if (isNaN(nextDateObj.getTime())) return nextExecution;

    // Direct formatting to DD-MM-YYYY
    const day = String(nextDateObj.getDate()).padStart(2, "0");
    const month = String(nextDateObj.getMonth() + 1).padStart(2, "0");
    const year = nextDateObj.getFullYear();

    return `${day}-${month}-${year}`;
  };

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-[100001]">
          <div className="w-[800px] bg-white shadow-xl flex flex-col h-full modal-slide">
            <div className="flex justify-between items-center w-full px-4 py-2 border-b">
              <h2 className="text-lg font-semibold">
                {isViewMode
                  ? "View Task"
                  : isEditMode
                    ? "Edit Task"
                    : "Add Task"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-xl px-3 focus:outline-none hover:bg-[#0fa9a6] rounded-full hover:text-white font-semibold text-black"
              >
                X
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-2">
              <form
                onSubmit={
                  !isViewMode ? handleSubmit : (e) => e.preventDefault()
                }
                className="flex flex-col gap-5"
                id="form-id"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Module <span className="text-red-500">*</span>
                    </label>
                    {renderModuleSelect(selectedModule, isViewMode)}
                    {errors.module && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.module}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Task <span className="text-red-500">*</span>
                    </label>
                    {renderInputOrText(task, isViewMode, setTask)}
                    {errors.task && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.task}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Topic <span className="text-red-500">*</span>
                    </label>
                    {renderTopicField(topicId, isViewMode)}
                    {errors.topic && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.topic}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Region <span className="text-red-500">*</span>
                    </label>
                    {renderRegionSelect(selectedRegionId, isViewMode)}
                    {errors.region && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.region}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Premises <span className="text-red-500">*</span>
                    </label>
                    {renderPremisesSelect(premises, isViewMode)}
                    {errors.premises && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.premises}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Responsible Person <span className="text-red-500">*</span>
                    </label>
                    {renderResponsiblePersonSelect(
                      responsiblePersonList,
                      isViewMode
                    )}
                    {errors.reference && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.reference}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium">
                      Frequency <span className="text-red-500">*</span>
                    </label>
                    {renderFrequencySelect(frequency, isViewMode)}
                    {errors.frequency && !isViewMode && (
                      <p className="text-red-500 text-sm">{errors.frequency}</p>
                    )}
                  </div>
                  {renderMonthlyIntervalSelect(monthlyInterval, isViewMode)}
                  {renderWeeklyIntervalSelect(weeklyInterval, isViewMode)}
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={isRecurring}
                      onChange={(e) =>
                        !isViewMode && setIsRecurring(e.target.checked)
                      }
                      disabled={isViewMode || frequency === "One Time"}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-2"
                      id="recurring-checkbox"
                    />
                    <label
                      htmlFor="recurring-checkbox"
                      className="text-base mt-2 font-medium"
                    >
                      Recurring Task
                    </label>
                  </div>
                  <div>
                    {renderDateInput(
                      fromDueDate,
                      setFromDueDate,
                      "fromDueDate",
                      "From Due Date",
                      isViewMode
                    )}
                  </div>
                  {/* {isRecurring && frequency !== "One Time" && (
                    <div>
                      {renderDateInput(
                        toDueDate,
                        setToDueDate,
                        "toDueDate",
                        "To Due Date",
                        isViewMode,
                        frequency !== "Daily"
                      )}
                    </div>
                  )} */}
                  {isRecurring && frequency !== "One Time" && (
                    <div>
                      <label className="block text-sm font-medium">
                        Next Execution Date
                      </label>
                      <div className="w-full border-2 border-gray-200 rounded font-semibold p-2 mt-1 bg-gray-50">
                        {getNextExecutionDate()}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    Description <span className="text-red-500">*</span>
                  </label>
                  {renderTextareaOrText(furtherDetails, isViewMode)}
                  {errors.furtherDetails && !isViewMode && (
                    <p className="text-red-500 text-sm">
                      {errors.furtherDetails}
                    </p>
                  )}
                </div>
              </form>
            </div>

            <div className="w-full px-3 py-4 bg-[#eef4f8] text-[#566680] border-t">
              <button
                className={
                  isViewMode
                    ? "bg-gray-300 text-gray-700 py-2 px-4 rounded-full cursor-not-allowed"
                    : "bg-teal-500 w-full text-white py-2 px-4 rounded-full hover:bg-teal-600"
                }
                type="submit"
                form={isViewMode ? "" : "form-id"}
              >
                SUBMIT
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddTask;
